import { FC, FocusEvent } from 'react';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import classNames from 'classnames';
import ru from 'react-phone-number-input/locale/ru.json';

import { Field, ControlSize, controlSizeClasses, useField } from '../field';

interface Props {
  size: ControlSize;
  label?: string;
  /** Classes for the `Field` around the input — layout only. */
  classNameWrap?: string;
  error?: string;
  showError?: boolean;
  required?: boolean;
  selected: string;
  onChange: (a: string) => void;
  onBlur?: (e: string) => void;
  showRequired?: boolean;
  disabled?: boolean;
}

type SizedPhoneProps = Pick<Props, 'selected' | 'onChange' | 'onBlur' | 'disabled'> & { size: ControlSize };

const SizedPhone: FC<SizedPhoneProps> = ({ size, selected, onChange, onBlur, disabled }) => {
  const field = useField();

  return (
    <PhoneInput
      id={field?.id}
      aria-invalid={field?.invalid || undefined}
      aria-describedby={field?.describedBy}
      // The library renders the flag and the <input> itself: its nodes can only be reached from here.
      className={classNames(
        'w-full rounded-control border bg-surface text-body-l text-ink focus-within:ring-1',
        '[&>input]:bg-transparent [&>input]:outline-none [&>div>.PhoneInputCountrySelectArrow]:hidden',
        controlSizeClasses[size],
        field?.invalid
          ? 'border-danger focus-within:ring-danger'
          : 'border-line-strong focus-within:border-ink focus-within:ring-ink',
        disabled && 'cursor-not-allowed bg-surface-muted text-ink-subtle',
      )}
      labels={ru}
      placeholder="+7 (999) 999 99 99"
      international
      defaultCountry="RU"
      disabled={disabled}
      value={selected || undefined}
      onChange={(value) => onChange(String(value))}
      onBlur={(e: FocusEvent<HTMLInputElement>) => onBlur?.(e.target.value)}
    />
  );
};

export const PhoneInputWithMask: FC<Props> = ({
  size,
  label = '',
  classNameWrap = '',
  required,
  error = '',
  showError = true,
  selected,
  onChange,
  showRequired = false,
  onBlur,
  disabled,
}) => {
  return (
    <Field
      size={size}
      label={label}
      required={required}
      requiredHint={showRequired}
      error={showError ? error : undefined}
      invalid={Boolean(error)}
      className={classNameWrap}
    >
      <SizedPhone size={size} selected={selected} onChange={onChange} onBlur={onBlur} disabled={disabled} />
    </Field>
  );
};
